import { useLayoutEffect } from 'react'

/** Containers whose children are the rows, not the container itself. */
const FLATTEN = 'form, .space-y-4'

const rowsOf = (parent: Element): Element[] =>
  Array.from(parent.children).flatMap((child) =>
    child.matches(FLATTEN) ? rowsOf(child) : child.tagName === 'DIALOG' ? [] : [child],
  )

/**
 * Enters a screen's rows one after another (`stagger` timing, globals.css
 * `[data-stagger-row]`) when the screen ARRIVES, looking through a form and its
 * field group so a field is a row of its own rather than the whole form fading
 * as one.
 *
 * Rows are tagged in a layout effect, so the first paint already has them
 * hidden. The root is watched for children being added, which is how a route's
 * content shows up: the outlet swaps it a commit after the frame around it, so
 * the frame cannot be keyed to the route. A batch of new rows counts from
 * zero. Rows that join deeper (a validation message, the other contact field)
 * are not children of the root, so nothing there is tagged and nothing
 * replays on a re-render.
 *
 * `Stagger rows` is how a screen asks for this; `enabled` lets that one
 * component call it unconditionally.
 */
export function useStaggerRows(root: HTMLElement | null, enabled = true): void {
  useLayoutEffect(() => {
    if (!enabled || root === null) return

    const tag = () => {
      rowsOf(root)
        .filter((row) => !row.hasAttribute('data-stagger-row'))
        .forEach((row, i) => {
          row.setAttribute('data-stagger-row', '')
          ;(row as HTMLElement).style.setProperty('--i', String(i))
        })
    }

    tag()
    const observer = new MutationObserver((records) => {
      if (records.some((record) => record.addedNodes.length > 0)) tag()
    })
    observer.observe(root, { childList: true })
    return () => {
      observer.disconnect()
    }
  }, [root, enabled])
}
